
/// <reference path="AccountingJournalTemplateGroupHeaderView.ts" />
/// <reference path="AccountingJournalTemplateGroupLineCell.ts" />
/// <reference path="AccountingJournalTemplateConditionCell.ts" />

class AccountingJournalTemplateViewController extends MUIViewController implements AccountingJournalTemplateGroupHeaderViewDelegate
{
    // Pseudo-domain in the domain combobox, not an AccountingEventType. A
    // template authored under it belongs to ONE analytic account (stored with
    // eventType 0) and the server matches it by the source's analytic tag.
    static readonly analyticAccountDomain = 100;
    // Filter combobox value that lists every analytic account's rules at once.
    private static readonly allAnalyticAccounts = "all";

    static newInstance(): AccountingJournalTemplateViewController {
        let vc = new AccountingJournalTemplateViewController("accounting-journal-template-detail-view");
        vc.initWithResource("layout/setup/AccountingJournalTemplateDetailView.html");
        return vc;
    }

    private domainComboBox: MUIComboBox = null;
    private analyticFilterComboBox: MUIComboBox = null;
    private analyticAccountDropdown: MUIButton = null;
    private titleLabel: MUILabel = null;
    private generateButton: MUIButton = null;
    private addGroupButton: MUIButton = null;
    private saveButton: MUIButton = null;
    private tableView: UITableView = null;

    private _selectedEventType: number = -1;
    private _selectedAnalyticAccount: AnalyticAccount|null = null;
    // Analytic domain with "all" picked in the filter: every account-owned
    // template is listed, grouped per account. Rules stay editable, but new
    // groups need a single template selected.
    private _showAllAnalytic: boolean = false;
    private _template: AccountingJournalTemplate|null = null;
    // Accounts that already own a template, in the filter combobox order.
    private _filterAccounts: AnalyticAccount[] = [];

    viewDidLoad() {
        super.viewDidLoad();

        this.domainComboBox = MUIOutlet(this, "domain-cb", "MUIComboBox");
        if (this.domainComboBox) this.domainComboBox.setOnChangeAction(this, function(this:AccountingJournalTemplateViewController, control: MUIComboBox, value: string) {
            this._selectedEventType = parseInt(value);
            let isAnalytic = this._selectedEventType == AccountingJournalTemplateViewController.analyticAccountDomain;
            this.setAnalyticControlsVisible(isAnalytic);
            this._selectedAnalyticAccount = null;
            if (this.analyticAccountDropdown) this.analyticAccountDropdown.title = MIOLocalizeString("SELECT_ANALYTIC_ACCOUNT", "SELECT ANALYTIC ACCOUNT");
            if (isAnalytic) {
                // Start from every account's rules; the filter narrows to one.
                this.reloadFilterAccounts(function(this:AccountingJournalTemplateViewController) {
                    this.selectFilterValue(AccountingJournalTemplateViewController.allAnalyticAccounts);
                    this.showAllAnalyticTemplates();
                });
            }
            else {
                this.loadTemplateForEventType(this._selectedEventType);
            }
        });

        // Only shown for the ANALYTIC ACCOUNT domain: narrows the rules to one
        // of the accounts that already own a template, without walking the
        // whole analytic tree. "All" lists every account's rules.
        this.analyticFilterComboBox = MUIOutlet(this, "analytic-filter-cb", "MUIComboBox");
        if (this.analyticFilterComboBox) this.analyticFilterComboBox.setOnChangeAction(this, function(this:AccountingJournalTemplateViewController, control: MUIComboBox, value: string) {
            if (value == AccountingJournalTemplateViewController.allAnalyticAccounts) {
                this._selectedAnalyticAccount = null;
                if (this.analyticAccountDropdown) this.analyticAccountDropdown.title = MIOLocalizeString("SELECT_ANALYTIC_ACCOUNT", "SELECT ANALYTIC ACCOUNT");
                this.showAllAnalyticTemplates();
                return;
            }
            let account = this._filterAccounts.find(function(a) { return a.identifier == value; }) ?? null;
            if (account == null) return;
            this._selectedAnalyticAccount = account;
            if (this.analyticAccountDropdown) this.analyticAccountDropdown.title = account.title;
            this.loadTemplateForEventType(this._selectedEventType);
        });

        // Only shown for the ANALYTIC ACCOUNT domain: picks any account (the
        // tree picker, with inline creation) to edit or start its template.
        this.analyticAccountDropdown = MUIOutlet(this, "analytic-account-dd", "MUIButton");
        if (this.analyticAccountDropdown) this.analyticAccountDropdown.setAction(this, function(this:AccountingJournalTemplateViewController) {
            AppHelper.sharedInstance().showSelectAnalyticAccountViewControllerFromView(this.analyticAccountDropdown, this._selectedAnalyticAccount, this, function(this:AccountingJournalTemplateViewController, controller:any, account:AnalyticAccount) {
                this._selectedAnalyticAccount = account;
                this.analyticAccountDropdown.title = account?.title ?? MIOLocalizeString("SELECT_ANALYTIC_ACCOUNT", "SELECT ANALYTIC ACCOUNT");
                if (account == null) {
                    this.selectFilterValue(AccountingJournalTemplateViewController.allAnalyticAccounts);
                    this.showAllAnalyticTemplates();
                    return;
                }
                this.addFilterAccountIfMissing(account);
                this.selectFilterValue(account.identifier);
                this.loadTemplateForEventType(this._selectedEventType);
            });
        });
        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        this.generateButton = MUIOutlet(this, "generate-btn", "MUIButton");
        if (this.generateButton) this.generateButton.setAction(this, function(this:AccountingJournalTemplateViewController) {
            this.generateJournalEntries();
        });

        // Analytic-only controls stay hidden until that domain is picked.
        this.setAnalyticControlsVisible(false);

        this.addGroupButton = MUIOutlet(this, "add-group-btn", "MUIButton");
        if (this.addGroupButton) this.addGroupButton.setAction(this, function(this:AccountingJournalTemplateViewController) {
            this.addGroup();
        });

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        if (this.saveButton) this.saveButton.setAction(this, function() {
            DBHelper.saveMainContext();
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animate?) {
        super.viewWillAppear(animate);
        this.updateUI();
    }

    // MARK: - Domain selection

    private setAnalyticControlsVisible(visible: boolean) {
        if (this.analyticFilterComboBox) this.analyticFilterComboBox.hidden = !visible;
        if (this.analyticAccountDropdown) this.analyticAccountDropdown.hidden = !visible;
        // Generation works for every domain (its event type) and for the analytic one.
        if (this.generateButton) this.generateButton.hidden = this._selectedEventType < 0;
    }

    // Accounts that own a template (eventType 0 = analytic pseudo-domain),
    // sorted by their path in the analytic tree so siblings stay together.
    private reloadFilterAccounts(completion: () => void) {
        let predicate = MIOPredicate.predicateWithFormat("eventType == 0 AND analyticAccount != null");
        DBHelper.queryObjectsWithCompletion("AccountingJournalTemplate", null, predicate, ["analyticAccount"], this, function(this: AccountingJournalTemplateViewController, templates: AccountingJournalTemplate[]) {
            let accounts: AnalyticAccount[] = [];
            let seen: { [id: string]: boolean } = {};
            for (let index = 0; index < templates.length; index++) {
                let account = templates[index].analyticAccount;
                if (account == null || seen[account.identifier] == true) continue;
                seen[account.identifier] = true;
                accounts.push(account);
            }
            accounts.sort(function(a, b) { return a.titlePath.localeCompare(b.titlePath); });
            this._filterAccounts = accounts;
            this.populateFilterComboBox();
            completion.call(this);
        });
    }

    private populateFilterComboBox() {
        if (this.analyticFilterComboBox == null) return;
        this.analyticFilterComboBox.removeAllItems();
        this.analyticFilterComboBox.addItem(MIOLocalizeString("ALL", "ALL"), AccountingJournalTemplateViewController.allAnalyticAccounts);
        for (let index = 0; index < this._filterAccounts.length; index++) {
            let account = this._filterAccounts[index];
            this.analyticFilterComboBox.addItem(account.titlePath, account.identifier);
        }
    }

    private selectFilterValue(value: string) {
        if (this.analyticFilterComboBox) this.analyticFilterComboBox.selectItem(value);
    }

    // An account picked from the tree that has no template yet joins the
    // filter list right away, so the combobox reflects what is on screen.
    private addFilterAccountIfMissing(account: AnalyticAccount) {
        for (let index = 0; index < this._filterAccounts.length; index++) {
            if (this._filterAccounts[index].identifier == account.identifier) return;
        }
        this._filterAccounts.push(account);
        this._filterAccounts.sort(function(a, b) { return a.titlePath.localeCompare(b.titlePath); });
        this.populateFilterComboBox();
    }

    private showAllAnalyticTemplates() {
        this._showAllAnalytic = true;
        this._template = null;
        if (this.addGroupButton) this.addGroupButton.setEnabled(false);
        this.fetchedResultsController = null;
        this.rebuildSections();
        this.tableView.reloadData();
    }

    private loadTemplateForEventType(eventType: number) {
        let isAnalytic = eventType == AccountingJournalTemplateViewController.analyticAccountDomain;
        this._showAllAnalytic = false;

        // Nothing to edit yet: no domain, or the analytic domain without an
        // account picked.
        if (eventType < 0 || (isAnalytic && this._selectedAnalyticAccount == null)) {
            this._template = null;
            if (this.addGroupButton) this.addGroupButton.setEnabled(false);
            this.fetchedResultsController = null;
            this._groupedSections = [];
            this.tableView.reloadData();
            return;
        }

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let predicateFormat = isAnalytic
            ? "eventType == 0 AND analyticAccount.identifier == '" + this._selectedAnalyticAccount.identifier + "'"
            : "eventType == " + eventType + " AND analyticAccount == null";
        let predicate = MIOPredicate.predicateWithFormat(predicateFormat);
        DBHelper.queryObjectWithCompletion("AccountingJournalTemplate", null, predicate, ["lines", "groups"], this, function(this: AccountingJournalTemplateViewController, template: AccountingJournalTemplate) {
            if (template != null) {
                this._template = template;
            } else {
                this._template = MIOEntityDescription.insertNewObjectForEntityForName("AccountingJournalTemplate", ad.managedObjectContext) as AccountingJournalTemplate;
                this._template.identifier = MIOUUID.UUID().UUIDString;
                this._template.eventType = isAnalytic ? 0 : eventType;
                this._template.isActive = true;
                this._template.analyticAccount = isAnalytic ? this._selectedAnalyticAccount : null;
            }
            if (this.addGroupButton) this.addGroupButton.setEnabled(true);
            this.updateUI();
        });
    }

    private updateUI() {
        if (this.viewIsLoaded == false) return;
        if (this._template == null && this._showAllAnalytic == false) return;
        this.fetchedResultsController = null;
        this.rebuildSections();
        this.tableView.reloadData();
    }

    // MARK: - Journal entry generation

    // Event type slugs the generate endpoint accepts (AccountingEventType.from(slug:)).
    // POS receipts and archived invoices have no journal-entry back-pointer, so they
    // cannot be scanned for pending sources.
    private static readonly generationSlugsByEventType: { [key: number]: string } = {
        1: "sales-invoice",
        2: "purchase-invoice",
        5: "bank-entry"
    };
    // Sources handled per request; the screen keeps calling while the server reports more.
    private static readonly generationBatchSize = 200;

    // Runs the rules of the current selection over the sources that still have
    // no journal entry: the selected account's tagged sources, every account's
    // (in the "all" view) or the whole domain by event type. A dry run first
    // tells the user how many will be processed before anything is written.
    private generateJournalEntries() {
        let scope: AccountingJournalGenerationScope;
        let scopeTitle: string;
        if (this._selectedEventType == AccountingJournalTemplateViewController.analyticAccountDomain) {
            if (this._showAllAnalytic == false && this._selectedAnalyticAccount == null) return;
            scope = { analyticAccount: this._showAllAnalytic ? null : this._selectedAnalyticAccount.identifier };
            scopeTitle = this._showAllAnalytic
                ? MIOLocalizeString("ALL_ANALYTIC_ACCOUNTS_WITH_RULES", "ALL ANALYTIC ACCOUNTS WITH RULES")
                : this._selectedAnalyticAccount.title;
        }
        else {
            let slug = AccountingJournalTemplateViewController.generationSlugsByEventType[this._selectedEventType];
            if (slug == null) {
                AppHelper.showInfoMessage(this, MIOLocalizeString("GENERATE_JOURNAL_ENTRIES", "GENERATE JOURNAL ENTRIES"), MIOLocalizeString("GENERATION_NOT_AVAILABLE_FOR_THIS_DOMAIN", "GENERATION IS NOT AVAILABLE FOR THIS DOMAIN"));
                return;
            }
            scope = { eventType: slug };
            scopeTitle = this.domainComboBox?.getSelectedItemText() ?? slug;
        }
        let title = MIOLocalizeString("GENERATE_JOURNAL_ENTRIES", "GENERATE JOURNAL ENTRIES");

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.generateAccountingJournalEntries(scope, true, null, (json:any, error:string) => {
            if (error != null) return; // the web service already reported it
            let pending = json?.Pending ?? 0;
            if (pending == 0) {
                AppHelper.showInfoMessage(this, title, MIOLocalizeString("NO_PENDING_SOURCES_TO_GENERATE", "NO PENDING MOVEMENTS OR INVOICES TO GENERATE"));
                return;
            }

            let avc = new MUIAlertViewController();
            avc.initWithTitle(title, scopeTitle + ": " + pending + " " + MIOLocalizeString("PENDING_SOURCES_TO_GENERATE", "MOVEMENTS AND INVOICES WITHOUT JOURNAL ENTRY WILL BE PROCESSED"), MUIAlertViewStyle.Default);
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("GENERATE", "GENERATE"), MUIAlertActionStyle.Default, this, function(this:AccountingJournalTemplateViewController) {
                this.runJournalGeneration(scope, title, pending, { generated: 0, withErrors: [], failed: [] });
            }));
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));
            this.presentViewController(avc, true);
        });
    }

    // One batch per request. Sources that got their entry leave the pending set,
    // so the next request takes the following ones; the loop ends when the server
    // reports nothing more or when a batch made no progress (every source failed).
    private runJournalGeneration(scope: AccountingJournalGenerationScope, title: string, pending: number, totals: { generated: number, withErrors: any[], failed: any[] }) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.generateAccountingJournalEntries(scope, false, AccountingJournalTemplateViewController.generationBatchSize, (json:any, error:string) => {
            if (error != null) {
                if (totals.generated > 0 || totals.withErrors.length > 0 || totals.failed.length > 0) this.showGenerationSummary(title, pending, totals);
                return;
            }
            let items: any[] = json?.Items ?? [];
            let generatedNow = json?.Generated ?? 0;
            totals.generated += generatedNow;
            totals.withErrors = totals.withErrors.concat(items.filter(function(item) { return item.Failure == null && (item.Errors ?? []).length > 0; }));
            totals.failed = totals.failed.concat(items.filter(function(item) { return item.Failure != null; }));

            let progressed = generatedNow + (json?.WithErrors ?? 0) > 0;
            if (json?.HasMore == true && progressed) {
                this.runJournalGeneration(scope, title, pending, totals);
                return;
            }
            this.showGenerationSummary(title, pending, totals);
        });
    }

    private showGenerationSummary(title: string, pending: number, totals: { generated: number, withErrors: any[], failed: any[] }) {
        let msg = totals.generated + "/" + pending + " " + MIOLocalizeString("JOURNAL_ENTRIES_GENERATED", "JOURNAL ENTRIES GENERATED");
        if (totals.withErrors.length > 0) msg += ". " + MIOLocalizeString("WITH_ERRORS", "WITH ERRORS") + " (" + totals.withErrors.length + "): " + this.referenceSummary(totals.withErrors);
        if (totals.failed.length > 0) msg += ". " + MIOLocalizeString("FAILED", "FAILED") + " (" + totals.failed.length + "): " + this.referenceSummary(totals.failed);
        AppHelper.showInfoMessage(this, title, msg);
    }

    // Keeps the summary alert readable when many sources are affected.
    private referenceSummary(items: any[]): string {
        let max = 10;
        let refs = items.slice(0, max).map(function(item) { return item.Reference ?? item.SourceID; });
        let text = refs.join(", ");
        if (items.length > max) text += " (+" + (items.length - max) + ")";
        return text;
    }

    // MARK: - Add group

    private addGroup() {
        if (this._template == null) return;
        let avc = new MUIAlertViewController();
        avc.initWithTitle(
            MIOLocalizeString("NEW_GROUP", "NEW GROUP"),
            MIOLocalizeString("ENTER_GROUP_NAME", "ENTER THE GROUP NAME"),
            MUIAlertViewStyle.Default
        );

        avc.addTextFieldWithConfigurationHandler(this, function(textField: MUITextField) {
            textField.setPlaceholderText(MIOLocalizeString("NAME", "NAME"));
        });

        avc.addComboBoxWithConfigurationHandler(this, function(this:AccountingJournalTemplateViewController, comboBox: MUIComboBox) {
            comboBox.addItem(MIOLocalizeString("BY_DOCUMENT", "By Document"), 0);
            comboBox.addItem(MIOLocalizeString("BY_LINE", "By Line"), 1);
            comboBox.addItem(MIOLocalizeString("BY_LINK", "By Link"), 2);
            comboBox.selectItem(0);
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD", "ADD"), MUIAlertActionStyle.Default, this, function() {
            let name = avc.textFields[0].text.trim();
            if (name.length > 0) {
                let matchMode = parseInt(avc.comboBoxes[0].getSelectedItem());
                let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
                let group = MIOEntityDescription.insertNewObjectForEntityForName("AccountingJournalGroupTemplate", ad.managedObjectContext) as AccountingJournalGroupTemplate;
                group.identifier = MIOUUID.UUID().UUIDString;
                group.title = name;
                group.template = this._template;
                group.isActive = true;
                group.matchMode = matchMode;
                group.orderRank = this.orderRankAfterLast(this._template);

                DBHelper.saveMainContext();
            }
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    // MARK: - Fractional indexing helpers

    private getAllFRCObjects(): AccountingJournalLineTemplate[] {
        let section = this.fetchedResultsController?.sections?.[0];
        if (section == null) return [];
        let count = section.numberOfObjects();
        let result: AccountingJournalLineTemplate[] = [];
        for (let i = 0; i < count; i++) {
            result.push(this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(i, 0)) as AccountingJournalLineTemplate);
        }
        return result;
    }

    // Order ranks only need to be consistent inside one template: in the
    // "all" view the fetch spans several, so ranks are computed against the
    // lines of the template the edited group belongs to.
    private getTemplateObjects(template: AccountingJournalTemplate|null): AccountingJournalLineTemplate[] {
        let objects = this.getAllFRCObjects();
        if (template == null) return objects;
        return objects.filter(function(obj) { return obj.template != null && obj.template.identifier == template.identifier; });
    }

    private generateOrderRank(prev: string|null, next: string|null): string {
        if (prev == null && next == null) return "n";
        if (next == null) return prev + "n";
        if (prev == null) {
            let first = next.charCodeAt(0);
            let a = 97;
            if (first - a > 1) return String.fromCharCode(a + Math.floor((first - a) / 2));
            return String.fromCharCode(a) + this.generateOrderRank(null, next.length > 1 ? next.substring(1) : null);
        }
        let i = 0;
        let prefix = "";
        while (i < prev.length && i < next.length && prev.charAt(i) === next.charAt(i)) { prefix += prev.charAt(i); i++; }
        let pc = i < prev.length ? prev.charCodeAt(i) : 96;
        let nc = i < next.length ? next.charCodeAt(i) : 123;
        if (nc - pc > 1) return prefix + String.fromCharCode(pc + Math.floor((nc - pc) / 2));
        return prefix + String.fromCharCode(pc) + this.generateOrderRank(
            i + 1 < prev.length ? prev.substring(i + 1) : null,
            i + 1 < next.length ? next.substring(i + 1) : null
        );
    }

    private orderRankAfterLast(template: AccountingJournalTemplate|null): string {
        let objects = this.getTemplateObjects(template);
        if (objects.length == 0) return this.generateOrderRank(null, null);
        return this.generateOrderRank(objects[objects.length - 1].orderRank ?? null, null);
    }

    private orderRankForNewCondition(group: AccountingJournalGroupTemplate): string {
        let objects = this.getTemplateObjects(group.template);
        let groupIdx = -1;
        for (let i = 0; i < objects.length; i++) {
            if (objects[i].identifier == group.identifier) { groupIdx = i; break; }
        }
        if (groupIdx < 0) return this.orderRankAfterLast(group.template);
        let lastConditionIdx = groupIdx;
        for (let i = groupIdx + 1; i < objects.length; i++) {
            let obj = objects[i];
            if (obj instanceof AccountingJournalGroupConditionTemplate && obj.parent != null && obj.parent.identifier == group.identifier) {
                lastConditionIdx = i;
            } else { break; }
        }
        let prev = objects[lastConditionIdx].orderRank ?? null;
        let nextIdx = lastConditionIdx + 1;
        let next = nextIdx < objects.length ? (objects[nextIdx].orderRank ?? null) : null;
        return this.generateOrderRank(prev, next);
    }

    private orderRankForNewEntryInGroup(group: AccountingJournalGroupTemplate): string {
        let objects = this.getTemplateObjects(group.template);
        let lastGroupItemIdx = -1;
        for (let i = 0; i < objects.length; i++) {
            let obj = objects[i];
            if (obj.identifier == group.identifier || (obj.parent != null && obj.parent.identifier == group.identifier)) {
                lastGroupItemIdx = i;
            }
        }
        if (lastGroupItemIdx < 0) return this.orderRankAfterLast(group.template);
        let prev = objects[lastGroupItemIdx].orderRank ?? null;
        let nextIdx = lastGroupItemIdx + 1;
        let next = nextIdx < objects.length ? (objects[nextIdx].orderRank ?? null) : null;
        return this.generateOrderRank(prev, next);
    }

    // MARK: - Grouped sections data

    private _groupedSections: { group: AccountingJournalGroupTemplate, items: AccountingJournalLineTemplate[] }[] = [];

    private rebuildSections() {
        let objects = this.getAllFRCObjects();
        let groups: AccountingJournalGroupTemplate[] = [];
        let childrenMap: { [id: string]: AccountingJournalLineTemplate[] } = {};

        for (let i = 0; i < objects.length; i++) {
            let obj = objects[i];
            if (obj instanceof AccountingJournalGroupTemplate) {
                groups.push(obj);
                childrenMap[obj.identifier] = [];
            }
        }

        for (let i = 0; i < objects.length; i++) {
            let obj = objects[i];
            if (obj instanceof AccountingJournalGroupTemplate) continue;
            if (obj.parent != null && childrenMap[obj.parent.identifier] != null) {
                childrenMap[obj.parent.identifier].push(obj);
            }
        }

        // The "all" view lists several templates: keep each template's groups
        // together, templates ordered like the filter combobox (account path),
        // and inside a template the fetch order (orderRank) is preserved.
        if (this._showAllAnalytic) {
            let self = this;
            let rankOf = function(group: AccountingJournalGroupTemplate): number {
                let account = group.template?.analyticAccount ?? null;
                if (account == null) return self._filterAccounts.length;
                for (let index = 0; index < self._filterAccounts.length; index++) {
                    if (self._filterAccounts[index].identifier == account.identifier) return index;
                }
                return self._filterAccounts.length;
            };
            let indexed = groups.map(function(g, i) { return { group: g, index: i, rank: rankOf(g) }; });
            indexed.sort(function(a, b) { return a.rank != b.rank ? a.rank - b.rank : a.index - b.index; });
            groups = indexed.map(function(entry) { return entry.group; });
        }

        this._groupedSections = groups.map(function(g) { return { group: g, items: childrenMap[g.identifier] }; });
    }

    // Account title shown before the group title in the "all" view.
    private groupTitlePrefix(group: AccountingJournalGroupTemplate): string|null {
        if (this._showAllAnalytic == false) return null;
        return group.template?.analyticAccount?.title ?? group.template?.analyticAccount?.identifier ?? null;
    }

    // MARK: - Table view data source
    // Each section = one group card
    // Row 0 = GroupCell (card header)
    // Row 1..N = Conditions + Entries

    numberOfSections(tableview) {
        return this._groupedSections.length;
    }

    numberOfRowsInSection(tableview, section: number) {
        return this._groupedSections[section].items.length + 1; // +1 group header
    }

    cellAtIndexPath(tableview, indexPath: MIOIndexPath) {
        let sectionData = this._groupedSections[indexPath.section];
        let lastRow = sectionData.items.length;
        let cell: UITableViewCell = null;

        // Row 0 = group header
        if (indexPath.row == 0) {
            let groupCell = tableview.dequeueReusableCellWithIdentifier("GroupCell") as AccountingJournalTemplateGroupHeaderView;
            groupCell.delegate = this;
            groupCell.titlePrefix = this.groupTitlePrefix(sectionData.group);
            groupCell.group = sectionData.group;
            cell = groupCell;
        }
        // Rows 1..N = conditions / entries
        else {
            let item = sectionData.items[indexPath.row - 1];

            if (item instanceof AccountingJournalGroupConditionTemplate) {
                let condCell = tableview.dequeueReusableCellWithIdentifier("ConditionCell") as AccountingJournalTemplateConditionCell;
                condCell.eventType = this._selectedEventType;
                condCell.item = item as AccountingJournalGroupConditionTemplate;
                cell = condCell;
            } else {
                let entryCell = tableview.dequeueReusableCellWithIdentifier("EntryCell") as AccountingJournalTemplateEntryCell;
                entryCell.eventType = this._selectedEventType;
                entryCell.item = item as AccountingJournalEntryTemplate;
                cell = entryCell;
            }
        }

        // Apply card position classes
        cell.layer.classList.remove("card-first", "card-middle", "card-last");
        if (indexPath.row == 0 && lastRow == 0) {
            cell.layer.classList.add("card-first");
            cell.layer.classList.add("card-last");
        } else if (indexPath.row == 0) {
            cell.layer.classList.add("card-first");
        } else if (indexPath.row == lastRow) {
            cell.layer.classList.add("card-last");
        } else {
            cell.layer.classList.add("card-middle");
        }

        return cell;
    }

    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) : any {
        return MUITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView, editingStyle:UITableViewCellEditingStyle, indexPath:MIOIndexPath) {
        if (editingStyle != UITableViewCellEditingStyle.Delete) return;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let sectionData = this._groupedSections[indexPath.section];

        if (indexPath.row == 0) {
            // Delete group + all its children
            for (let i = 0; i < sectionData.items.length; i++) {
                ad.managedObjectContext.deleteObject(sectionData.items[i]);
            }
            ad.managedObjectContext.deleteObject(sectionData.group);
        } else {
            let item = sectionData.items[indexPath.row - 1];
            ad.managedObjectContext.deleteObject(item);
        }

        ad.managedObjectContext.save();
    }

    // MARK: - AccountingJournalTemplateGroupHeaderViewDelegate

    addConditionDidPress(header: AccountingJournalTemplateGroupHeaderView, group: AccountingJournalGroupTemplate) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let condition = MIOEntityDescription.insertNewObjectForEntityForName("AccountingJournalGroupConditionTemplate", ad.managedObjectContext) as AccountingJournalGroupConditionTemplate;
        condition.identifier = MIOUUID.UUID().UUIDString;
        condition.parent = group;
        condition.template = group.template ?? this._template;
        condition.operation = 0;
        condition.orderRank = this.orderRankForNewCondition(group);

        DBHelper.saveMainContext();
    }

    addEntryDidPress(header: AccountingJournalTemplateGroupHeaderView, group: AccountingJournalGroupTemplate) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let line = MIOEntityDescription.insertNewObjectForEntityForName("AccountingJournalEntryTemplate", ad.managedObjectContext) as AccountingJournalEntryTemplate;
        line.identifier = MIOUUID.UUID().UUIDString;
        line.template = group.template ?? this._template;
        line.parent = group;
        line.orderRank = this.orderRankForNewEntryInGroup(group);

        DBHelper.saveMainContext();
    }

    // MARK: - Fetched results controller

    private _fetchedResultsController: MIOFetchedResultsController = null;
    set fetchedResultsController(value) {
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
        this._fetchedResultsController = value;
    }

    get fetchedResultsController() {
        if (this._fetchedResultsController != null) return this._fetchedResultsController;
        if (this._template == null && this._showAllAnalytic == false) return null;

        let ad = MUIWebApplication.sharedInstance().delegate;
        let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey("orderRank", true)];
        // eventType 0 is only ever stored by analytic account templates.
        let predicate = this._showAllAnalytic
            ? "template.eventType == 0"
            : "template.identifier == '" + this._template.identifier + "'";
        let fetchRequest = DBHelper.listFetchRequestWithEntityName("AccountingJournalLineTemplate", sortDescriptors, predicate);
        fetchRequest.relationshipKeyPathsForPrefetching = ["ledgerAccount", "parent", "template", "template.analyticAccount"];

        let frc = new MIOFetchedResultsController();
        frc.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        frc.delegate = this;
        frc.performFetch();

        this._fetchedResultsController = frc;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller) {
        this.rebuildSections();
        this.tableView.reloadData();
    }
}
